import React, { useEffect, useState } from 'react';
import { CreditCard, Clock, CheckCircle, PauseCircle, RefreshCw, KeyRound } from 'lucide-react';
import { useMemberPortal } from './MemberPortal';
import { getMyMemberships, submitRenewalRequest, getMyLocker, getMyRenewalRequests } from '../../lib/memberDb';
import { differenceInDays, parseISO, format } from 'date-fns';
import { ko } from 'date-fns/locale';

const statusConfig = {
  active: { label: '이용중', color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20', icon: CheckCircle },
  expired: { label: '만료', color: 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700', icon: Clock },
  frozen: { label: '일시정지', color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20', icon: PauseCircle },
};

function RenewalButton({ memberRecord }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [note, setNote] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitError, setSubmitError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    const { error } = await submitRenewalRequest(memberRecord.id, { note });
    setSubmitting(false);
    if (!error) {
      setSubmitted(true);
      setShowForm(false);
    } else {
      setSubmitError('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  }

  if (submitted) {
    return (
      <div className="mt-3 text-center">
        <p className="text-xs text-green-600 dark:text-green-400">연장 신청이 완료되었습니다. 관리자가 확인 후 연락드립니다.</p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-primary-200 dark:border-primary-800 text-primary-600 dark:text-primary-400 text-sm font-medium hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> 연장 신청
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-2">
          <textarea
            className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 resize-none"
            rows={2}
            placeholder="요청사항 (선택)"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
          {submitError && (
            <p className="text-xs text-red-500 text-center">{submitError}</p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2 rounded-xl border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400">취소</button>
            <button type="submit" disabled={submitting} className="flex-1 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60">
              {submitting ? '신청 중...' : '신청'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function MyMembership() {
  const { memberRecord } = useMemberPortal();
  const [memberships, setMemberships] = useState([]);
  const [locker, setLocker] = useState(null);
  const [renewalHistory, setRenewalHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getMyMemberships(memberRecord.id),
      getMyLocker(memberRecord.id),
      getMyRenewalRequests(memberRecord.id),
    ]).then(([m, l, r]) => {
      setMemberships(m);
      setLocker(l);
      setRenewalHistory(r || []);
    }).catch(e => {
      console.error('회원권 로드 실패:', e);
    }).finally(() => {
      setLoading(false);
    });
  }, [memberRecord.id]);

  const active = memberships.find(m => m.status === 'active' || m.status === 'frozen');
  const past = memberships.filter(m => m.status === 'expired');

  if (loading) return <div className="p-4 text-center text-gray-400 text-sm pt-12">불러오는 중...</div>;

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">내 회원권</h2>

      {/* 현재 회원권 */}
      {active ? (
        <div className="card p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">현재 이용중</p>
              <p className="font-bold text-gray-900 dark:text-white text-lg">{active.productName}</p>
            </div>
            {(() => {
              const cfg = statusConfig[active.status] || statusConfig.active;
              const Icon = cfg.icon;
              return (
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                  <Icon className="w-3.5 h-3.5" /> {cfg.label}
                </span>
              );
            })()}
          </div>

          {active.status === 'active' && active.endDate && (() => {
            const today = new Date();
            const end = parseISO(active.endDate);
            const total = differenceInDays(end, parseISO(active.startDate));
            const remaining = differenceInDays(end, today);
            const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
            return (
              <>
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>잔여</span>
                    <span className={remaining <= 7 ? 'text-red-500 font-medium' : ''}>{remaining}일 남음</span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${remaining <= 7 ? 'bg-red-500' : 'bg-primary-500'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500">
                  <span>{format(parseISO(active.startDate), 'yyyy.MM.dd')}</span>
                  <span>{format(end, 'yyyy.MM.dd')}</span>
                </div>
              </>
            );
          })()}

          {active.status === 'frozen' && (
            <p className="text-sm text-blue-600 dark:text-blue-400">
              {active.frozenAt && `${format(parseISO(active.frozenAt), 'MM.dd', { locale: ko })}부터 일시정지 중`}
            </p>
          )}
          <RenewalButton memberRecord={memberRecord} />
        </div>
      ) : (
        <div className="card p-6 text-center space-y-3">
          <CreditCard className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto" />
          <p className="text-sm text-gray-500 dark:text-gray-400">현재 이용중인 회원권이 없습니다.</p>
          <RenewalButton memberRecord={memberRecord} />
        </div>
      )}

      {/* 락커 정보 */}
      {locker && (() => {
        const daysLeft = locker.endDate ? differenceInDays(parseISO(locker.endDate), new Date()) : null;
        return (
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <KeyRound className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">락커</p>
                <p className="font-bold text-gray-900 dark:text-white">{locker.number}번</p>
              </div>
            </div>
            {locker.endDate && (
              <>
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{locker.startDate ? format(parseISO(locker.startDate), 'yyyy.MM.dd') : ''}</span>
                  <span className={daysLeft !== null && daysLeft <= 7 ? 'text-red-500 font-medium' : ''}>
                    {daysLeft !== null && daysLeft < 0 ? '만료됨' : daysLeft !== null ? `${daysLeft}일 남음` : ''}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  {locker.startDate && (() => {
                    const total = differenceInDays(parseISO(locker.endDate), parseISO(locker.startDate));
                    const pct = total > 0 ? Math.max(0, Math.min(100, ((daysLeft ?? 0) / total) * 100)) : 0;
                    return (
                      <div
                        className={`h-full rounded-full transition-all ${daysLeft !== null && daysLeft <= 7 ? 'bg-red-500' : 'bg-blue-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    );
                  })()}
                </div>
                <p className="text-xs text-gray-400 mt-1 text-right">
                  만료: {format(parseISO(locker.endDate), 'yyyy.MM.dd')}
                </p>
              </>
            )}
          </div>
        );
      })()}

      {/* 이용 내역 */}
      {past.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">이용 내역</p>
          <div className="space-y-2">
            {past.map(m => (
              <div key={m.id} className="card p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{m.productName}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {m.startDate && m.endDate
                      ? `${format(parseISO(m.startDate), 'yyyy.MM.dd')} ~ ${format(parseISO(m.endDate), 'yyyy.MM.dd')}`
                      : ''}
                  </p>
                </div>
                <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">만료</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 연장 신청 내역 */}
      {renewalHistory.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">연장 신청 내역</p>
          <div className="space-y-2">
            {renewalHistory.map(r => {
              const statusMap = {
                pending: { label: '대기중', color: 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/20' },
                approved: { label: '승인', color: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20' },
                rejected: { label: '거절', color: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20' },
              };
              const st = statusMap[r.status] || statusMap.pending;
              return (
                <div key={r.id} className="card p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {r.createdAt ? format(parseISO(r.createdAt), 'yyyy.MM.dd') : ''}
                    </p>
                    {r.note && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.note}</p>}
                    {r.processedNote && <p className="text-xs text-gray-400 mt-0.5">관리자: {r.processedNote}</p>}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
